import React, { useState } from 'react';
import { ArrowLeftIcon, EditIcon, DownloadIcon, FileTextIcon, UserIcon, CalendarIcon, DollarSignIcon, MinusSquareIcon, CheckCircleIcon, AlertCircleIcon, LinkIcon, ExternalLinkIcon } from 'lucide-react';
import { formatCurrency } from '../utils/currencyFormatter';
import { NoteVisibility } from '../common/StandardizedNotes';
import { GlobeIcon, UsersIcon, LockIcon, MessageSquareIcon } from 'lucide-react';
export function CreditNoteDetail({
  creditNote,
  onBack,
  onEdit,
  locale = 'en-US'
}) {
  // Function to determine badge color based on status
  const getStatusBadgeClass = status => {
    switch (status) {
      case 'Approved':
        return 'bg-green-100 text-green-800';
      case 'Pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'Draft':
        return 'bg-gray-100 text-gray-800';
      case 'Posted':
        return 'bg-blue-100 text-blue-800';
      case 'Rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  // Added state for related communications
  const [relatedCommunications, setRelatedCommunications] = useState([{
    id: 4,
    title: 'Office Supplies Return - Credit Note Clarification',
    timestamp: '2023-10-19T16:20:00Z',
    messageCount: 1,
    lastMessage: "Credit note received for returned items from last month's order. Quality issues were reported to the vendor."
  }]);
  // Function to render note visibility icon and label
  const renderNoteVisibility = () => {
    if (!creditNote.noteVisibility) return null;
    const getVisibilityIcon = () => {
      switch (creditNote.noteVisibility) {
        case 'Public':
          return <GlobeIcon className="h-4 w-4 text-green-500" data-id="element-3274" />;
        case 'Internal':
          return <UsersIcon className="h-4 w-4 text-blue-500" data-id="element-3275" />;
        case 'Role-based':
          return <UsersIcon className="h-4 w-4 text-purple-500" data-id="element-3276" />;
        case 'User-specific':
          return <UserIcon className="h-4 w-4 text-orange-500" data-id="element-3277" />;
        case 'Creator-only':
          return <LockIcon className="h-4 w-4 text-red-500" data-id="element-3278" />;
        default:
          return null;
      }
    };
    const getVisibilityLabel = () => {
      switch (creditNote.noteVisibility) {
        case 'Public':
          return 'Public - Visible to everyone';
        case 'Internal':
          return 'Internal - Visible to all internal users';
        case 'Role-based':
          return `Role-based - Visible to specific roles: ${creditNote.noteVisibilityDetails || 'None specified'}`;
        case 'User-specific':
          return `User-specific - Visible to specific users: ${creditNote.noteVisibilityDetails || 'None specified'}`;
        case 'Creator-only':
          return 'Private - Visible only to creator';
        default:
          return '';
      }
    };
    return <div className="flex items-center text-xs text-gray-500 mt-1" data-id="element-3279">
        {getVisibilityIcon()}
        <span className="ml-1" data-id="element-3280">{getVisibilityLabel()}</span>
      </div>;
  };
  // Group credit note lines by invoice
  const linesByInvoice = {};
  if (creditNote.lines) {
    creditNote.lines.forEach(line => {
      if (line.linkedInvoiceId) {
        if (!linesByInvoice[line.linkedInvoiceId]) {
          linesByInvoice[line.linkedInvoiceId] = [];
        }
        linesByInvoice[line.linkedInvoiceId].push(line);
      }
    });
  }
  // Get invoice details by ID
  const getInvoiceById = invoiceId => {
    return creditNote.linkedInvoices.find(inv => inv.id === invoiceId) || null;
  };
  return <div className="bg-white shadow-lg rounded-lg overflow-hidden" data-id="element-3281">
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center" data-id="element-3282">
        <h3 className="text-lg font-medium text-gray-900 flex items-center" data-id="element-3283">
          <MinusSquareIcon className="h-5 w-5 mr-2 text-green-600" data-id="element-3284" />
          Credit Note Details
        </h3>
        <div className="flex space-x-2" data-id="element-3285">
          <button onClick={onBack} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-3286">
            <ArrowLeftIcon className="h-4 w-4 mr-1" data-id="element-3287" />
            Back to List
          </button>
          {(creditNote.status === 'Draft' || creditNote.status === 'Rejected') && <button onClick={() => onEdit(creditNote)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-3288">
              <EditIcon className="h-4 w-4 mr-1" data-id="element-3289" />
              Edit
            </button>}
          <button className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-3290">
            <DownloadIcon className="h-4 w-4 mr-1" data-id="element-3291" />
            Download PDF
          </button>
        </div>
      </div>
      <div className="p-6" data-id="element-3292">
        {/* Header Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-3293">
          <div data-id="element-3294">
            <div className="flex items-center mb-4" data-id="element-3295">
              <FileTextIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-3296" />
              <h4 className="text-lg font-medium text-gray-900" data-id="element-3297">
                Credit Note Information
              </h4>
            </div>
            <div className="bg-gray-50 rounded-md p-4 space-y-3" data-id="element-3298">
              <div className="flex justify-between" data-id="element-3299">
                <span className="text-sm font-medium text-gray-500" data-id="element-3300">
                  Credit Note Number:
                </span>
                <span className="text-sm text-gray-900 font-semibold" data-id="element-3301">
                  {creditNote.creditNoteNumber}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-3302">
                <span className="text-sm font-medium text-gray-500" data-id="element-3303">
                  Status:
                </span>
                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(creditNote.status)}`} data-id="element-3304">
                  {creditNote.status}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-3305">
                <span className="text-sm font-medium text-gray-500" data-id="element-3306">Date:</span>
                <span className="text-sm text-gray-900" data-id="element-3307">
                  {new Date(creditNote.creditNoteDate).toLocaleDateString(locale)}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-3308">
                <span className="text-sm font-medium text-gray-500" data-id="element-3309">
                  Reference:
                </span>
                <span className="text-sm text-gray-900" data-id="element-3310">
                  {creditNote.reference || 'N/A'}
                </span>
              </div>
            </div>
          </div>
          <div data-id="element-3311">
            <div className="flex items-center mb-4" data-id="element-3312">
              <UserIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-3313" />
              <h4 className="text-lg font-medium text-gray-900" data-id="element-3314">
                Vendor Information
              </h4>
            </div>
            <div className="bg-gray-50 rounded-md p-4 space-y-3" data-id="element-3315">
              <div className="flex justify-between" data-id="element-3316">
                <span className="text-sm font-medium text-gray-500" data-id="element-3317">
                  Vendor Name:
                </span>
                <span className="text-sm text-gray-900 font-semibold" data-id="element-3318">
                  {creditNote.vendor}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-3319">
                <span className="text-sm font-medium text-gray-500" data-id="element-3320">
                  Currency:
                </span>
                <span className="text-sm text-gray-900" data-id="element-3321">
                  {creditNote.currency}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-3322">
                <span className="text-sm font-medium text-gray-500" data-id="element-3323">
                  Total Amount:
                </span>
                <span className="text-sm text-gray-900 font-semibold" data-id="element-3324">
                  {formatCurrency(creditNote.amount, creditNote.currency, locale)}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-3325">
                <span className="text-sm font-medium text-gray-500" data-id="element-3326">
                  Applied Amount:
                </span>
                <div className="flex items-center" data-id="element-3327">
                  {creditNote.appliedAmount > 0 ? creditNote.unappliedAmount > 0 ? <AlertCircleIcon className="h-4 w-4 text-yellow-500 mr-1" data-id="element-3328" /> : <CheckCircleIcon className="h-4 w-4 text-green-500 mr-1" data-id="element-3329" /> : <AlertCircleIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-3330" />}
                  <span className="text-sm text-gray-900" data-id="element-3331">
                    {formatCurrency(creditNote.appliedAmount || 0, creditNote.currency, locale)}
                  </span>
                </div>
              </div>
              <div className="flex justify-between" data-id="element-3332">
                <span className="text-sm font-medium text-gray-500" data-id="element-3333">
                  Unapplied Amount:
                </span>
                <span className="text-sm text-gray-900" data-id="element-3334">
                  {formatCurrency(creditNote.unappliedAmount || 0, creditNote.currency, locale)}
                </span>
              </div>
            </div>
          </div>
        </div>
        {/* Line Items */}
        <div className="mt-6" data-id="element-3335">
          <h3 className="text-lg font-medium text-gray-900" data-id="element-3336">Line Items</h3>
          <div className="mt-4 overflow-x-auto" data-id="element-3337">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-3338">
              <thead className="bg-gray-50" data-id="element-3339">
                <tr data-id="element-3340">
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3341">
                    Description
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3342">
                    Quantity
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3343">
                    Unit Price
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3344">
                    Tax Code
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3345">
                    Tax Amount
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3346">
                    Amount
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3347">
                    Invoice Ref
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3348">
                    Invoice Line Ref
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-3349">
                {creditNote.lines.map((line, index) => <tr key={index} data-id="element-3350">
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900" data-id="element-3351">
                      {line.description}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-3352">
                      {line.quantity}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-3353">
                      {formatCurrency(line.unitPrice, creditNote.currency, locale)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-3354">
                      {line.taxCode} ({line.taxRate}%)
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-3355">
                      {formatCurrency(line.taxAmount, creditNote.currency, locale)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-3356">
                      {formatCurrency(line.amount, creditNote.currency, locale)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-3357">
                      {line.invoiceRef ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800" data-id="element-3358">
                          {line.invoiceRef}
                        </span> : <span className="text-gray-400" data-id="element-3359">—</span>}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-3360">
                      {line.invoiceLineRef ? <span className="text-gray-700" data-id="element-3361">
                          {line.invoiceLineRef}
                        </span> : <span className="text-gray-400" data-id="element-3362">—</span>}
                    </td>
                  </tr>)}
              </tbody>
              <tfoot className="bg-gray-50" data-id="element-3363">
                <tr data-id="element-3364">
                  <td colSpan={4} className="px-3 py-2 text-right text-sm font-medium text-gray-500" data-id="element-3365">
                    Subtotal:
                  </td>
                  <td colSpan={4} className="px-3 py-2 text-sm text-gray-900" data-id="element-3366">
                    {formatCurrency(creditNote.lines.reduce((sum, line) => sum + line.quantity * line.unitPrice, 0), creditNote.currency, locale)}
                  </td>
                </tr>
                <tr data-id="element-3367">
                  <td colSpan={4} className="px-3 py-2 text-right text-sm font-medium text-gray-500" data-id="element-3368">
                    Tax Total:
                  </td>
                  <td colSpan={4} className="px-3 py-2 text-sm text-gray-900" data-id="element-3369">
                    {formatCurrency(creditNote.lines.reduce((sum, line) => sum + (line.taxAmount || 0), 0), creditNote.currency, locale)}
                  </td>
                </tr>
                <tr data-id="element-3370">
                  <td colSpan={4} className="px-3 py-2 text-right text-sm font-medium text-gray-900" data-id="element-3371">
                    Total:
                  </td>
                  <td colSpan={4} className="px-3 py-2 text-sm font-bold text-gray-900" data-id="element-3372">
                    {formatCurrency(creditNote.amount, creditNote.currency, locale)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
        {/* Linked Invoices */}
        <div className="mb-8" data-id="element-3373">
          <div className="flex items-center mb-4" data-id="element-3374">
            <LinkIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-3375" />
            <h4 className="text-lg font-medium text-gray-900" data-id="element-3376">
              Linked Invoices
            </h4>
            <span className="ml-2 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded" data-id="element-3377">
              Reference only
            </span>
          </div>
          {creditNote.linkedInvoices && creditNote.linkedInvoices.length > 0 ? <div className="overflow-x-auto" data-id="element-3378">
              <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-md" data-id="element-3379">
                <thead className="bg-gray-50" data-id="element-3380">
                  <tr data-id="element-3381">
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3382">
                      Invoice Number
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3383">
                      Date
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3384">
                      Amount
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3385">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-3386">
                  {creditNote.linkedInvoices.map(invoice => <tr key={invoice.id} data-id="element-3387">
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-3388">
                        {invoice.invoiceNumber || invoice.number}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500" data-id="element-3389">
                        {new Date(invoice.invoiceDate || invoice.date).toLocaleDateString(locale)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900" data-id="element-3390">
                        {formatCurrency(invoice.amount, invoice.currency || creditNote.currency, locale)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm" data-id="element-3391">
                        <button className="text-indigo-600 hover:text-indigo-900 text-sm flex items-center" data-id="element-3392">
                          <ExternalLinkIcon className="h-4 w-4 mr-1" data-id="element-3393" />
                          View Invoice
                        </button>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div> : <div className="bg-gray-50 p-4 text-center rounded-md border border-gray-200" data-id="element-3394">
              <p className="text-gray-500" data-id="element-3395">
                No invoices linked to this credit note
              </p>
            </div>}
        </div>
        {/* Notes */}
        {creditNote.notes && <div className="mb-8" data-id="element-3396">
            <div className="flex items-center mb-4" data-id="element-3397">
              <FileTextIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-3398" />
              <h4 className="text-lg font-medium text-gray-900" data-id="element-3399">Notes</h4>
            </div>
            <div className="bg-gray-50 p-4 rounded-md border border-gray-200" data-id="element-3400">
              {renderNoteVisibility()}
              <p className="text-sm text-gray-700 mt-2" data-id="element-3401">{creditNote.notes}</p>
            </div>
          </div>}
        {/* Related Communications - New Section */}
        {relatedCommunications.length > 0 && <div className="mb-8" data-id="element-3402">
            <div className="flex items-center justify-between mb-4" data-id="element-3403">
              <div className="flex items-center" data-id="element-3404">
                <MessageSquareIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-3405" />
                <h4 className="text-lg font-medium text-gray-900" data-id="element-3406">
                  Related Communications
                </h4>
              </div>
              <a href="#" className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center" data-id="element-3407">
                View All
                <ExternalLinkIcon className="h-4 w-4 ml-1" data-id="element-3408" />
              </a>
            </div>
            <div className="bg-gray-50 rounded-md border border-gray-200 divide-y divide-gray-200" data-id="element-3409">
              {relatedCommunications.map(comm => <div key={comm.id} className="p-4 hover:bg-gray-100" data-id="element-3410">
                  <div className="flex justify-between" data-id="element-3411">
                    <h5 className="text-sm font-medium text-indigo-600" data-id="element-3412">
                      {comm.title}
                    </h5>
                    <span className="text-xs text-gray-500" data-id="element-3413">
                      {new Date(comm.timestamp).toLocaleDateString(locale)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1 line-clamp-2" data-id="element-3414">
                    {comm.lastMessage}
                  </p>
                  <div className="mt-2 flex justify-between items-center" data-id="element-3415">
                    <span className="text-xs text-gray-500" data-id="element-3416">
                      {comm.messageCount}{' '}
                      {comm.messageCount === 1 ? 'message' : 'messages'}
                    </span>
                    <a href="#" className="text-xs text-indigo-600 hover:text-indigo-800" data-id="element-3417">
                      View Thread
                    </a>
                  </div>
                </div>)}
            </div>
          </div>}
      </div>
    </div>;
}